import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'

/**
 * PAM 批量导入弹窗
 * 解析 CSV 数据并批量导入 Admob 价格到中介组
 */
function PamImportModal({ isOpen, onClose, selectedAppId, mediationGroups, onConfirm }) {
  const [csvData, setCsvData] = useState('')
  const [previewData, setPreviewData] = useState(null)
  const [errors, setErrors] = useState([])

  const handleCheckData = () => {
    const lines = csvData
      .trim()
      .split('\n')
      .filter((line) => line.trim())

    if (lines.length < 2) {
      setErrors(['至少需要 2 行数据（组名 + 价格）'])
      setPreviewData(null)
      return
    }

    // Parse CSV (line 2 = group names, line 3+ = admob prices)
    const groupNamesLine = lines[1]
    const delimiter = groupNamesLine.includes('\t') ? '\t' : ','
    
    const groupNames = groupNamesLine.split(delimiter).map((name) => name.trim())
    const priceLines = lines.slice(2).map((line) =>
      line.split(delimiter).map((val) => val.trim()),
    )

    // Parse groups with prices
    const parsedGroups = []
    const newErrors = []

    groupNames.forEach((groupName, colIndex) => {
      if (!groupName) return

      const prices = []
      priceLines.forEach((row) => {
        const cell = row[colIndex]
        if (cell) {
          const price = parseFloat(cell)
          if (!isNaN(price) && price > 0) {
            prices.push(price)
          }
        }
      })

      // Check if group exists
      const exists = mediationGroups?.some((g) => g.name === groupName)

      parsedGroups.push({
        name: groupName,
        exists,
        prices: [...new Set(prices)].sort((a, b) => b - a), // unique & sort desc
      })

      if (!exists) {
        newErrors.push(`未找到中介组 "${groupName}"`)
      }
    })

    setErrors(newErrors)
    setPreviewData({
      groups: parsedGroups,
      validCount: parsedGroups.filter((g) => g.exists).length,
      invalidCount: parsedGroups.filter((g) => !g.exists).length,
    })
  }

  const handleConfirmImport = () => {
    if (!previewData || previewData.validCount === 0) {
      return
    }

    // Pass valid groups to parent
    const validGroups = previewData.groups.filter((g) => g.exists)
    onConfirm(validGroups)
    
    // Reset state
    setCsvData('')
    setPreviewData(null)
    setErrors([])
  }

  const handleClose = () => {
    setCsvData('')
    setPreviewData(null)
    setErrors([])
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>批量导入中介组</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Input Section */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              粘贴 CSV 数据（Tab 或逗号分隔）
            </label>
            <Textarea
              value={csvData}
              onChange={(e) => setCsvData(e.target.value)}
              placeholder={`示例格式:\n第1行（忽略）\n组名1,组名2,组名3\n42,35,30\n32,28,25\n20,15,10`}
              className="font-mono text-sm h-40"
            />
            <p className="text-xs text-gray-500">
              第1行：忽略 | 第2行：组名 | 第3行+：Admob 价格
            </p>
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <Alert variant="destructive">
              <AlertDescription>
                <ul className="list-disc list-inside">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Preview Section */}
          {previewData && (
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-2">数据预览</h4>
              <p className="text-sm text-gray-600 mb-4">
                解析到 {previewData.groups.length} 个组 | 有效：{' '}
                {previewData.validCount} | 无效：{previewData.invalidCount}
              </p>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-2 text-left">组名</th>
                      <th className="border p-2 text-left">状态</th>
                      <th className="border p-2 text-left">Admob 价格</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.groups.map((group, index) => (
                      <tr
                        key={index}
                        className={group.exists ? 'bg-green-50' : 'bg-red-50'}
                      >
                        <td className="border p-2">{group.name}</td>
                        <td className="border p-2">
                          {group.exists ? '✓ 已找到' : '✗ 未找到'}
                        </td>
                        <td className="border p-2">
                          {group.prices.length > 0
                            ? group.prices.join(', ')
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={handleCheckData}>
            检查数据
          </Button>
          <Button
            variant="default"
            onClick={handleConfirmImport}
            disabled={!previewData || previewData.validCount === 0}
          >
            预览修改
          </Button>
          <Button variant="destructive" onClick={handleClose}>
            取消
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default PamImportModal

